import React, { useEffect, useState } from "react";
import { FloatingLabel, Form, InputGroup, Row } from "react-bootstrap";
import MyCollapse from "../Common/MyCollapse";
import MyIcon from "../Common/MyIcon";
import HelpButton from "../HelpContent/HelpButton";
import BillingDetailsIcon from "../../assets/icons/invoice_history.svg2";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import {
  unitConversions,
  WeatherCodes,
  WeatherForecastOptions,
} from "../Monitoring/WeatherAlertConstants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faCircleMinus,
  faSquare,
} from "@fortawesome/free-solid-svg-icons";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import {
  useCreateWeatherDisplayMapColoursMutation,
  useGetClientUnitsQuery,
  useGetWeatherDisplayMapColoursQuery,
  useUpdateClientUnitsMutation,
} from "../store/slices/settingsSlice";
import { toast } from "react-toastify";

const { countries, defaultCountry, defaultTZ } = JSON.parse(
  document.querySelector("#app").dataset.page
).props;
export default function WeatherSettings() {
  const [weatherDisplayMapColours, setWeatherDisplayMapColours] = useState([]);
  const weatherDisplayMapColoursData = useGetWeatherDisplayMapColoursQuery();
  useEffect(() => {
    if (weatherDisplayMapColoursData?.data?.colours) {
      setWeatherDisplayMapColours(
        weatherDisplayMapColoursData?.data?.colours || []
      );
    }
  }, [weatherDisplayMapColoursData]);

  const [clientUnits, setClientUnits] = useState([]);
  const weatherDefaultUnitsData = useGetClientUnitsQuery();
  useEffect(() => {
    if (weatherDefaultUnitsData?.data) {
      setClientUnits(weatherDefaultUnitsData?.data || []);
    }
  }, [weatherDefaultUnitsData]);

  const [createColours] = useCreateWeatherDisplayMapColoursMutation();
  const [saveClientUnits] = useUpdateClientUnitsMutation();

  return (
    <div>
      <div className="bg-purple-gradient-darker-reverse rounded-4 p-3 ">
        <>
          <h5 className=" d-flex align-items-center">
            <MyIcon Icon={BillingDetailsIcon} className="me-2" />
            <span className="ms-2">
              <span className="fw-bold me-2">Weather settings:</span>
              <HelpButton helpKey="settings-organisation-settings-weather-settings" />

              <div className="small text-secondary">
                Manage weather settings for your organisation.
              </div>
            </span>
          </h5>
        </>

        <hr className="my-3 text-secondary" />
        <div className="mb-3">
          <Row>
            <h5>Preferred Units</h5>
            {Object.entries(unitConversions)
              .filter(([unitName, unit]) => unit.conversions.length > 1)
              .map(([unitName, unit]) => (
                <div className="col-6">
                  <FloatingLabel label={unit.type} className="mb-2">
                    <Form.Select
                      placeholder={unitName + " " + unit.unit}
                      onChange={(e) =>
                        setClientUnits((prev) => ({
                          ...prev,
                          [unit.type]: e.target.value,
                        }))
                      }
                      value={clientUnits?.[unit.type] || ""}
                    >
                      <option value="" disabled>
                        Select unit
                      </option>
                      {unit.conversions.map((unit) => (
                        <option value={unit.to}>{unit.label}</option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </div>
              ))}
          </Row>
        </div>
        <div className="mb-3">
          <h5>Preferred Colours</h5>
          <p>
            Please select a weather condition to configure colours. Then, enter
            min and max values to set the colour range. You can add multiple
            colour configurations for the same weather condition to create
            different colour ranges.
          </p>
          <p>
            Leave min/max empty for infinity. Leave both empty to cover all
            values with the same colour. The first match will be applied, so
            order your colour configurations from most specific to least
            specific for best results.
          </p>
          <div className="my-3 col-12">
            <InputGroup>
              <InputGroupText>Add colour configuration for </InputGroupText>
              <Form.Select
                placeholder={"Select a condition to configure colors"}
                onChange={(e) => {
                  setWeatherDisplayMapColours((prev) => {
                    const newColor = {
                      condition: e.target.value,
                      from:
                        e.target.value === "significantWeatherCode" ? "1" : "",
                      to:
                        e.target.value === "significantWeatherCode" ? "10" : "",
                      color: "#000000",
                    };

                    return [...prev, newColor];
                  });
                }}
                value={""}
              >
                <option value="" disabled>
                  Select condition
                </option>
                {WeatherForecastOptions.filter((c) => c.value !== "wind").map(
                  (cond) => (
                    <option value={cond.value}>{cond.label}</option>
                  )
                )}
              </Form.Select>
            </InputGroup>
          </div>

          {[
            ...new Set(weatherDisplayMapColours?.map((c) => c.condition) || []),
          ].map((conditionKey) => {
            const condition = WeatherForecastOptions.find(
              (cond) => cond.value === conditionKey
            );
            return (
              <div className="bg-purple-gradient rounded-3 p-2 mb-3">
                <div className="text-capitalize d-flex align-items-center justify-content-between mb-2">
                  <div className="">{condition?.label} </div>
                  <button
                    className="btn btn-sm btn-outline-danger border-0"
                    onClick={() =>
                      setWeatherDisplayMapColours((prev) => {
                        return prev.filter((c) => c.condition !== conditionKey);
                      })
                    }
                  >
                    Remove weather condition
                  </button>
                </div>
                {weatherDisplayMapColours
                  ?.filter((c) => c.condition === conditionKey)
                  ?.map(
                    (colorConfig, colorConfigIndex, conditionColourConfigs) => (
                      <div className="row mb-2 g-1" key={colorConfigIndex}>
                        <div className="col-4">
                          {colorConfig.condition ===
                          "significantWeatherCode" ? (
                            <div className="bg-white rounded-2 p-2">
                              Min value
                              <Input
                                value={
                                  colorConfig.from ? [colorConfig.from] : []
                                }
                                inputType="select"
                                onChange={([v]) =>
                                  setWeatherDisplayMapColours((prev) => {
                                    return prev.map((config) =>
                                      config === colorConfig
                                        ? { ...config, from: v }
                                        : config
                                    );
                                  })
                                }
                                options={WeatherCodes.map((code) => ({
                                  value: `${code.value}`,
                                  label: code.label,
                                }))}
                              />
                            </div>
                          ) : (
                            <FloatingLabel label={`Min value`}>
                              <Form.Control
                                placeholder="Min value"
                                value={colorConfig.from}
                                onChange={(e) =>
                                  setWeatherDisplayMapColours((prev) => {
                                    return prev.map((config) =>
                                      config === colorConfig
                                        ? { ...config, from: e.target.value }
                                        : config
                                    );
                                  })
                                }
                              />
                            </FloatingLabel>
                          )}
                        </div>
                        <div className="col-4">
                          {colorConfig.condition ===
                          "significantWeatherCode" ? (
                            <div className="bg-white rounded-2 p-2">
                              Max value
                              <Input
                                value={colorConfig.to ? [colorConfig.to] : []}
                                inputType="select"
                                onChange={([v]) =>
                                  setWeatherDisplayMapColours((prev) => {
                                    return prev.map((config) =>
                                      config === colorConfig
                                        ? { ...config, to: v }
                                        : config
                                    );
                                  })
                                }
                                options={WeatherCodes.map((code) => ({
                                  value: `${code.value}`,
                                  label: code.label,
                                }))}
                              />
                            </div>
                          ) : (
                            <FloatingLabel label={`Max value`}>
                              <Form.Control
                                placeholder="Max value"
                                value={colorConfig.to}
                                onChange={(e) =>
                                  setWeatherDisplayMapColours((prev) => {
                                    return prev.map((config) =>
                                      config === colorConfig
                                        ? { ...config, to: e.target.value }
                                        : config
                                    );
                                  })
                                }
                              />
                            </FloatingLabel>
                          )}
                        </div>
                        <div className="col">
                          <div className="bg-white rounded-2 d-flex h-100 align-items-end p-2">
                            <div className="flex-grow-1">
                              <Input
                                placeholder="Colour"
                                onChange={([v]) =>
                                  setWeatherDisplayMapColours((prev) => {
                                    return prev.map((config) =>
                                      config === colorConfig
                                        ? { ...config, color: v }
                                        : config
                                    );
                                  })
                                }
                                inputType="select"
                                value={
                                  colorConfig.color ? [colorConfig.color] : []
                                }
                                options={[
                                  { value: "#000000", label: "Black" },
                                  { value: "#FF0000", label: "Red" },
                                  { value: "#FFA500", label: "Orange" },
                                  { value: "#FFFF00", label: "Yellow" },
                                  { value: "#008000", label: "Green" },
                                  { value: "#0000FF", label: "Blue" },
                                  { value: "#800080", label: "Purple" },
                                  { value: "#808080", label: "Gray" },
                                  { value: "#FFFFFF", label: "White" },
                                ]}
                                formatOptionLabel={(o) => (
                                  <div>
                                    <FontAwesomeIcon
                                      icon={faSquare}
                                      style={{ color: o.value }}
                                    />
                                    {" " + o.label}
                                  </div>
                                )}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-auto align-self-center">
                          <button
                            className="btn btn-sm btn-outline-danger border-0 align-self-center"
                            onClick={() =>
                              setWeatherDisplayMapColours((prev) => {
                                return prev.filter(
                                  (config) => config !== colorConfig
                                );
                              })
                            }
                          >
                            <FontAwesomeIcon icon={faCircleMinus} />
                          </button>
                        </div>
                      </div>
                    )
                  )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="d-flex justify-content-end my-3">
        <button
          onClick={async () => {
            const res = await createColours(weatherDisplayMapColours);
            if ("data" in res) {
              toast.success("Colours saved successfully");
            }
            const res2 = await saveClientUnits(clientUnits);
            if ("data" in res2) {
              toast.success("Units saved successfully");
            }
          }}
          type="button"
          className="btn btn-colored-reverse rounded-pill fw-bold "
        >
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          SAVE WEATHER SETTINGS{" "}
        </button>
      </div>
      <JsonCollapse
        title="Current weather settings JSON"
        json={{ weatherDisplayMapColours }}
      />
    </div>
  );
}
